import React, { useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Alert, Button } from '@trussworks/react-uswds';
import * as yup from 'yup';
import classNames from 'classnames';
import { useSubmitHelpMutation } from '@api/services/help/hooks';
import { queryClient } from '@api/queryClient';
import { DASHBOARD_QUERY_KEYS } from '@api/services/dashboard';
import ErrorSummary from '@components/error-summary/ErrorSummary';
import { useSupportDrawer } from '@stores';
import { HelpRequestStatuses, ResponseStatuses } from '@src/utils/enums';
import { getAppStore } from '@src/utils/store-reference';
import Intro from './Intro';
import Questions from './Questions';
import Recommendation from './Recommendation';
import { FormValues, Recommendation as RequestAssistanceRecommendation, RequestAssistanceProps } from './types';
import {
  getHelpCategoryCodes,
  getRequestAssistanceRecommendation,
  hasRegulatorHelpCategory,
  mapRequestAssistanceFormValuesToAnswers,
} from './helpers';
import './requestAssistance.scss';

const defaultValues: FormValues = {
  helpCategories: [],
  reason: '',
};

const existingHelpInquiryMessage = 'An open or in-progress help inquiry already exists for this request.';

const validationSchema = yup.object().shape({
  helpCategories: yup.array().of(
    yup.object().shape({
      value: yup.string().required(),
      label: yup.string().required(),
    })
  ),
  reason: yup
    .string()
    .test(
      'reason-required-for-regulator-help',
      'Required',
      (value: string | undefined, context: { parent: FormValues }) => {
        const { helpCategories } = context.parent;

        if (!hasRegulatorHelpCategory(helpCategories)) {
          return true;
        }

        return Boolean(value?.trim());
      }
    ),
});

const RequestAssistanceContent: React.FC<RequestAssistanceProps> = ({ className, showSelfHelp = true }) => {
  const [hasStarted, setHasStarted] = useState(false);
  const [submittedRecommendation, setSubmittedRecommendation] = useState<RequestAssistanceRecommendation>(null);
  const [submitWarningMessage, setSubmitWarningMessage] = useState<string | null>(null);
  const store = getAppStore();
  const requestId = store.selectRequestID?.();
  const version = store.selectVersion?.();

  const methods = useForm<FormValues>({
    defaultValues,
    resolver: yupResolver(validationSchema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  const {
    formState: { errors, submitCount },
    handleSubmit,
  } = methods;

  const values = methods.watch();
  const answers = useMemo(() => mapRequestAssistanceFormValuesToAnswers(values), [values]);
  const recommendation = getRequestAssistanceRecommendation(answers);

  const submitHelpMutation = useSubmitHelpMutation({
    requestId,
    version,
    onSuccess: (response) => {
      if (response.status === ResponseStatuses.AlreadyExist) {
        setSubmitWarningMessage(response.msg || existingHelpInquiryMessage);
        return;
      }
      if (response.status === ResponseStatuses.Success) {
        queryClient.invalidateQueries({
          queryKey: [DASHBOARD_QUERY_KEYS.GET_DASHBOARD],
        });
        setSubmittedRecommendation(recommendation);
        store.doUpdateRequestObject({
          helpInquiryID: response.data.helpInquiryId,
          helpInquiryStatus: HelpRequestStatuses.Open,
        });
      }
    },
  });

  const containerStyles = classNames('request-assistance', className);
  const questionStyles = classNames('request-assistance-questions');
  const introTextStyles = classNames('text-base', 'margin-bottom-2');
  const recommendationStyles = classNames('margin-top-3');
  const actionStyles = classNames('display-flex', 'flex-align-center', 'gap-2', 'margin-top-3');

  const shouldShowErrorSummary = submitCount > 0 && Object.keys(errors || {}).length > 0;

  const resetRequestAssistance = () => {
    methods.reset(defaultValues);
    setHasStarted(false);
    setSubmittedRecommendation(null);
    setSubmitWarningMessage(null);
    submitHelpMutation.reset();
  };

  const handleStart = () => {
    setHasStarted(true);
    setSubmittedRecommendation(null);
    setSubmitWarningMessage(null);
    submitHelpMutation.reset();
  };

  const handleBackToIntro = () => {
    resetRequestAssistance();
  };

  const handleSubmitHelp = handleSubmit((formValues) => {
    if (recommendation !== 'regulatorAssistance') {
      return;
    }
    const helpCategories = getHelpCategoryCodes(formValues.helpCategories);

    if (helpCategories.length === 0) {
      return;
    }
    setSubmitWarningMessage(null);

    submitHelpMutation.mutate({
      reason: formValues.reason.trim(),
      helpCategories,
    });
  });

  if (!hasStarted) {
    return (
      <div className={containerStyles}>
        <Intro onContinue={handleStart} showSelfHelp={showSelfHelp} />
      </div>
    );
  }

  if (submittedRecommendation) {
    return (
      <div className={containerStyles}>
        <Alert type='success' heading='Regulator assistance requested' headingLevel='h3'>
          <p className='margin-bottom-0'>
            Your request for regulator assistance has been submitted. A regulator will respond to your inquiry within a
            few business days.
          </p>
        </Alert>

        <div className={actionStyles}>
          <Button type='button' onClick={handleBackToIntro} outline>
            Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <FormProvider {...methods}>
      <div className={containerStyles}>
        <div className={questionStyles}>
          <p className={introTextStyles}>
            Select the category or categories that best match your question. Regulator assistance is generally intended
            for regulatory or permitting questions, jurisdiction questions, guidance on what information to provide, or
            whether a pre-application request may be appropriate.
          </p>

          {shouldShowErrorSummary && <ErrorSummary defaultExpanded errors={errors} modalID={undefined} />}

          {submitWarningMessage && (
            <Alert type='warning' heading='Help inquiry already exists' headingLevel='h4' slim className='margin-top-3'>
              <p className='margin-bottom-0'>{submitWarningMessage}</p>
            </Alert>
          )}

          {submitHelpMutation.isError && !submitWarningMessage && (
            <Alert type='error' heading='Unable to submit request' headingLevel='h4' slim className='margin-top-3'>
              <p className='margin-bottom-0'>Please try again. If the issue continues, contact RRS Support.</p>
            </Alert>
          )}

          <Questions />

          <div className={recommendationStyles}>
            <Recommendation
              recommendation={recommendation}
              onBack={handleBackToIntro}
              onSubmit={handleSubmitHelp}
              isSubmitting={submitHelpMutation.isPending}
            />
          </div>

          {!recommendation && (
            <div className={actionStyles}>
              <Button type='button' onClick={handleBackToIntro} outline>
                Back
              </Button>
            </div>
          )}
        </div>
      </div>
    </FormProvider>
  );
};

const RequestAssistance: React.FC<RequestAssistanceProps> = (props) => {
  const isDrawerOpen = useSupportDrawer((state) => state.isDrawerOpen);
  if (!isDrawerOpen) {
    return null;
  }
  return <RequestAssistanceContent {...props} />;
};

export default RequestAssistance;
